import QRCode from 'qrcode';
import config from '../config';

export const printReceipt = async (sale, user, settings = {}) => {
  if (!sale) return;

  const paperSize = settings.paper_size || '80mm';
  const showLogo = settings.show_logo !== false && settings.show_logo !== 'false';
  const showTax = settings.show_tax !== false && settings.show_tax !== 'false';
  const showDiscount = settings.show_discount !== false && settings.show_discount !== 'false';
  
  const receiptHeader = settings.receipt_header ? settings.receipt_header.split('\n').map(line => `<div>${line}</div>`).join('') : '';
  const receiptFooter = settings.receipt_footer ? settings.receipt_footer.split('\n').map(line => `<div>${line}</div>`).join('') : `<div>Thank you for shopping!</div><div>Welcome back</div>`;

  const kraPin = settings.tax_number || '';
  const businessPhone = settings.business_phone || '';
  const businessEmail = settings.business_email || '';

  const apiUrl = new URL(config.apiUrl);
  const backendOrigin = `${apiUrl.protocol}//${apiUrl.host}`;
  const logoUrl = settings.logo_url ? `${backendOrigin}${settings.logo_url}` : null;

  let qrCodeDataUrl = '';
  try {
    const qrData = JSON.stringify({
      inv: sale.invoice_number,
      amt: sale.grand_total,
      pin: kraPin,
      dt: sale.created_at || Date.now()
    });
    qrCodeDataUrl = await QRCode.toDataURL(qrData, { 
      width: 120, 
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' }
    });
  } catch (err) {
    console.error('Failed to generate QR code', err);
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount).replace('KES', 'KSh');
  };

  const itemsHtml = (sale.items || []).map(item => `
    <div class="item">
      <div class="item-name">${item.product_name}</div>
      <div class="item-qty">${item.quantity} x ${formatCurrency(item.unit_price)}</div>
      <div class="item-price">${formatCurrency(item.total_amount)}</div>
    </div>
  `).join('');

  const customerHtml = sale.customer_first ? `<div>Customer: ${sale.customer_first} ${sale.customer_last}</div>` : '';
  const discountHtml = (showDiscount && parseFloat(sale.discount_total) > 0) ? `<div class="total-row"><span>Discount:</span><span>-${formatCurrency(sale.discount_total)}</span></div>` : '';
  const taxHtml = showTax ? `<div class="total-row"><span>Tax:</span><span>${formatCurrency(sale.tax_total)}</span></div>` : '';
  // 1. Create the container
  const printContainer = document.createElement('div');
  printContainer.id = 'pos-print-container';
  printContainer.innerHTML = `
    <div class="receipt-container">
      <div class="header">
        ${showLogo ? (logoUrl ? `<img src="${logoUrl}" class="receipt-logo" alt="Logo" />` : `<div class="business-name">${user?.business_name || 'Your Store'}</div>`) : ''}
        ${receiptHeader ? `<div class="custom-header">${receiptHeader}</div>` : ''}
        <div class="business-contacts">
          ${kraPin ? `<div>KRA PIN: ${kraPin}</div>` : ''}
          ${businessPhone ? `<div>Tel: ${businessPhone}</div>` : ''}
          ${businessEmail ? `<div>Email: ${businessEmail}</div>` : ''}
        </div>
        <div>${new Date(sale.created_at || Date.now()).toLocaleString()}</div>
        <div class="invoice-info">
          <div>Invoice: ${sale.invoice_number}</div>
          <div>Cashier: ${sale.cashier_name || user?.first_name} ${sale.cashier_lastname || user?.last_name}</div>
          ${customerHtml}
        </div>
      </div>
      <div class="items">
        ${itemsHtml}
      </div>
      <div class="totals">
        <div class="total-row"><span>Subtotal:</span><span>${formatCurrency(sale.subtotal)}</span></div>
        ${taxHtml}
        ${discountHtml}
        <div class="total-row grand-total"><span>Total:</span><span>${formatCurrency(sale.grand_total)}</span></div>
        <div class="total-row"><span>Paid (${sale.payment_method}):</span><span>${formatCurrency(sale.amount_paid)}</span></div>
        <div class="total-row"><span>Change:</span><span>${formatCurrency(sale.change_amount)}</span></div>
      </div>
      <div class="footer">
        ${receiptFooter}
        ${qrCodeDataUrl ? `<div class="qr-container"><img src="${qrCodeDataUrl}" alt="QR Code" class="receipt-qr" /></div>` : ''}
      </div>
    </div>
  `;
  document.body.appendChild(printContainer);

  // Measure the exact pixel height of the receipt content
  const contentHeight = printContainer.scrollHeight;
  const printHeight = contentHeight + 40; // Add a small buffer for the cutter

  // 2. Create the print styles
  const style = document.createElement('style');
  style.id = 'pos-print-style';
  style.innerHTML = `
    @media print {
      /* Hide the entire React app */
      #root { display: none !important; }
      
      /* Reset page margins and force exact custom height */
      @page { margin: 0; size: ${paperSize} ${paperSize === 'A4' ? 'auto' : printHeight + 'px'}; }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: ${printHeight}px !important;
        min-height: 0 !important;
        max-height: ${printHeight}px !important;
        background: white !important;
        overflow: hidden !important;
      }
      
      /* Show ONLY the receipt container */
      #pos-print-container {
        display: block !important;
        width: 100%;
        height: ${printHeight}px !important;
        margin: 0;
        padding: 0;
        font-family: 'Courier New', Courier, monospace;
        color: #000;
        font-size: 11px;
        overflow: hidden !important;
      }
      
      .receipt-container { padding: 5px; width: 100%; box-sizing: border-box; }
      .header { text-align: center; margin-bottom: 8px; }
      .receipt-logo { max-width: 150px; max-height: 80px; object-fit: contain; margin-bottom: 5px; filter: grayscale(100%); }
      .custom-header { font-size: 10px; margin-bottom: 4px; white-space: pre-wrap; font-weight: normal; }
      .business-name { font-size: 14px; font-weight: bold; margin-bottom: 2px; }
      .business-contacts { font-size: 10px; margin-bottom: 4px; }
      .invoice-info { margin: 5px 0; font-size: 10px; line-height: 1.2; text-align: left; }
      .items { width: 100%; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 5px 0; font-size: 10px; }
      .item { display: flex; justify-content: space-between; margin: 2px 0; }
      .item-name { flex: 2; word-break: break-all; padding-right: 5px; text-align: left; }
      .item-qty { flex: 1; text-align: center; }
      .item-price { flex: 1; text-align: right; }
      .totals { margin-top: 5px; font-size: 10px; }
      .total-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
      .grand-total { font-weight: bold; font-size: 12px; margin-top: 3px; padding-top: 3px; border-top: 1px dashed #000; }
      .footer { text-align: center; margin-top: 10px; font-size: 10px; padding-bottom: 5px; }
      .thank-you { font-weight: bold; }
      .qr-container { margin-top: 8px; text-align: center; }
      .receipt-qr { width: 120px; height: 120px; display: inline-block; }
    }
    
    /* Hide the receipt container on screen */
    @media screen {
      #pos-print-container { display: none !important; }
    }
  `;
  document.head.appendChild(style);

  // 3. Trigger the native print dialog
  setTimeout(() => {
    window.print();
    
    // 4. Cleanup after print dialog closes
    setTimeout(() => {
      if (document.body.contains(printContainer)) {
        document.body.removeChild(printContainer);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    }, 1000);
  }, 100);
};
